import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import type { LearningGrain } from '@/core/domain/learning/grain/LearningGrain.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import { FetchLearningGrainUseCase } from '@/core/useCases/learning/grain/FetchLearningGrainUseCase.ts'
import { UpdateLearningGrainUseCase } from '@/core/useCases/learning/grain/UpdateLearningGrainUseCase.ts'
import { UpdateLearningGrainStatusUseCase } from '@/core/useCases/learning/grain/UpdateLearningGrainStatusUseCase.ts'
import { CreateLearningGrainUseCase } from '@/core/useCases/learning/grain/CreateLearningGrainUseCase.ts'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'
import { UploadLearningSupportFileUseCase } from '@/core/useCases/learning/support/UploadLearningSupportFileUseCase.ts'
import type { UploadedSupportFile } from '@/core/infrastructure/schemas/learning/support/UploadedSupportFileSchema.ts'

// Statuts dont le message, rédigé en français par l'API, s'affiche tel quel (validation, taille)
const UPLOAD_DISPLAYABLE_STATUSES = [400, 413]

export const useLearningGrainsStore = defineStore('learningGrains', () => {
  // USECASES
  const fetchLearningGrainUseCase = DependencyContainer.resolve(FetchLearningGrainUseCase)
  const updateLearningGrainUseCase = DependencyContainer.resolve(UpdateLearningGrainUseCase)
  const updateLearningGrainStatusUseCase = DependencyContainer.resolve(
    UpdateLearningGrainStatusUseCase
  )
  const createLearningGrainUseCase = DependencyContainer.resolve(CreateLearningGrainUseCase)
  const uploadLearningSupportFileUseCase = DependencyContainer.resolve(
    UploadLearningSupportFileUseCase
  )

  // INSCRIPTION AUX EVENTS DES AUTRES STORES

  // BASE STORE
  const baseStore = createStoreBase([
    'fetchLearningGrain',
    'fetchAllLearningGrains',
    'updateLearningGrain',
    'updateLearningGrainStatus',
    'createLearningGrain',
    'uploadSupportFile'
  ])

  // STATE
  const grains: Ref<LearningGrain[]> = ref([])

  // MUTATIONS
  function setGrains(newGrains: LearningGrain[]) {
    grains.value = newGrains
  }

  function addGrains(newGrains: LearningGrain[]) {
    grains.value = grains.value.concat(newGrains)
  }

  function addGrain(grain: LearningGrain) {
    grains.value.push(grain)
  }

  function updateGrainData(grain: LearningGrain) {
    const index = grains.value.findIndex((g) => g.id === grain.id)
    if (index !== -1) {
      grains.value[index] = grain
    } else {
      addGrain(grain)
    }
  }

  function sortGrains() {
    grains.value.sort((a, b) => a.order - b.order)
  }

  // ACTIONS
  async function createGrain(
    learningGrain: LearningGrain,
    learningModuleId: number,
    learningCourseId: number
  ) {
    try {
      baseStore.loading.value.createLearningGrain = true
      baseStore.resetError()
      const response = await createLearningGrainUseCase.execute(
        learningCourseId,
        learningModuleId,
        learningGrain
      )

      console.log('store', response)
      addGrain(response)
      baseStore.showSuccessToast('Création de grain', 'Le grain a été créé avec succès.')

      return response
    } catch (error) {
      await handleError(error, 'Une erreur est survenue lors de la création du grain.')
    } finally {
      baseStore.loading.value.createLearningGrain = false
    }
    return Promise.reject()
  }

  async function updateGrain(
    learningGrain: LearningGrain,
    learningModuleId: number,
    learningCourseId: number
  ) {
    try {
      baseStore.loading.value.updateLearningGrain = true
      baseStore.resetError()

      const response = await updateLearningGrainUseCase.execute(
        learningCourseId,
        learningModuleId,
        learningGrain.id,
        learningGrain.toInputFormat()
      )

      updateGrainData(response)

      baseStore.showSuccessToast('Mise à jour de grain', 'Le grain a été mis à jour avec succès.')
      return response
    } catch (error) {
      await handleError(error, 'Une erreur est survenue lors de la mise à jour du grain.')

      return Promise.reject(error)
    } finally {
      baseStore.loading.value.updateLearningGrain = false
    }
  }

  async function updateGrainStatus(
    status: LearningStatus.Values,
    learningGrainId: number,
    learningModuleId: number,
    learningCourseId: number
  ) {
    try {
      baseStore.loading.value.updateLearningGrainStatus = true
      baseStore.resetError()

      const response = await updateLearningGrainStatusUseCase.execute(
        learningCourseId,
        learningModuleId,
        learningGrainId,
        status
      )

      updateGrainData(response)
      baseStore.showSuccessToast(
        'Mise à jour de statut',
        'Le statut du grain a été mis à jour avec succès.'
      )

      return response
    } catch (error) {
      await handleError(error, 'Une erreur est survenue lors de la mise à jour du statut du grain.')
      return Promise.reject(error)
    } finally {
      baseStore.loading.value.updateLearningGrainStatus = false
    }
  }

  async function uploadSupportFile(
    file: File,
    learningGrainId: number,
    learningModuleId: number,
    learningCourseId: number
  ): Promise<UploadedSupportFile> {
    try {
      baseStore.loading.value.uploadSupportFile = true
      baseStore.resetError()

      return await uploadLearningSupportFileUseCase.execute(
        learningCourseId,
        learningModuleId,
        learningGrainId,
        file
      )
    } catch (error) {
      const displayable =
        error instanceof NabooError && UPLOAD_DISPLAYABLE_STATUSES.includes(error.status)
      baseStore.showErrorToast(
        "Envoi du fichier",
        displayable
          ? (error as NabooError).message
          : "L'envoi du fichier a échoué, veuillez réessayer."
      )
      await handleError(error, "Une erreur est survenue lors de l'envoi du fichier.")

      return Promise.reject(error)
    } finally {
      baseStore.loading.value.uploadSupportFile = false
    }
  }

  async function deleteGrain(grainId: number, moduleId: number, courseId: number) {
    try {
      await updateGrainStatus(LearningStatus.Values.ARCHIVED, grainId, moduleId, courseId)

      storeEventBus.emit('learning:grain:deleted', {
        grainId,
        moduleId,
        courseId
      })
    } catch (error) {
      console.error('Error deleting grain:', error)
    }
  }

  function handleError(error: unknown, message: string) {
    if (!(error instanceof NabooError)) {
      error = new NabooError({
        message,
        status: 500,
        name: 'ModuleStoreError',
        code: 'MODULE_STORE_ERROR',
        originalError: error instanceof Error ? error : undefined
      })
    }

    baseStore.setError(error as NabooError)
    return Promise.reject(error)
  }

  return {
    ...baseStore,
    grains,
    createGrain,
    updateGrain,
    updateGrainStatus,
    uploadSupportFile,
    deleteGrain
  }
})
